import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { collection, doc, getDoc, getDocs, query, Timestamp, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import Menu from '../../panel/Menu';

function Allbookingreport() {
    const Toast = Swal.mixin({
        toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
    });

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUserData(user);
            if (user) fetchUsers(user.uid);
        });
        return () => unsubscribe();
    }, []);

    const printRef = useRef();
    const [loading, setloading] = useState(false);
    const [loadincount, setloadincount] = useState(true);
    const [userData, setUserData] = useState();
    const [fromDate, setfromDate] = useState("");
    const [toDate, settoDate] = useState("");
    const [username, setusername] = useState("");
    const [bookingData, setbookingData] = useState([]);
    const [canceledBookingData, setCanceledBookingData] = useState([]); // New state for Canceled Bookings
    const [payhistory, setpayhistory] = useState([]);
    const [paymodes, setpaymodes] = useState([]);
    const [total, settotal] = useState(0);
    const [totalCanceledAmount, setTotalCanceledAmount] = useState(0); // Track canceled loss totals

    const fetchUsers = async (uid) => {
        try {
            if (loadincount) {
                const docRef = doc(db, "users", uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setusername(docSnap.data().username);
                    setloadincount(false);
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getReport = async () => {
        // 1. Validation Check
        if (!fromDate || !toDate) {
            Toast.fire({ icon: "error", title: 'Please select From Date & To Date' });
            return;
        }

        setloading(true);
        setbookingData([]);
        setCanceledBookingData([]);
        setpaymodes([]);
        setpayhistory([]);

        try {
            // 2. Setup Precise Time Bounds for Payment History Timestamps (JS Date Objects)
            const startOfDay = new Date(fromDate);
            startOfDay.setHours(0, 0, 0, 0);

            const endOfDay = new Date(toDate);
            endOfDay.setHours(23, 59, 59, 999);

            // 3. Setup String Bounds for checkindetails (stored as YYYY-MM-DD strings)
            const startDateStr = moment(fromDate).format('YYYY-MM-DD');
            const endDateStr = moment(toDate).format('YYYY-MM-DD');

            // 4. Executing Range Queries in Parallel across Collections
            const [payHistorySnapshot, checkinDetailsSnapshot] = await Promise.all([
                getDocs(query(
                    collection(db, "payhistory"),
                    where("createdAt", ">=", Timestamp.fromDate(startOfDay)),
                    where("createdAt", "<=", Timestamp.fromDate(endOfDay)),
                    where("paymenttype", "==", "B"),
                    where("status", "==", "Working")
                )),
                getDocs(query(
                    collection(db, 'checkindetails'),
                    where('checkindate', '>=', startDateStr),
                    where('checkindate', '<=', endDateStr)
                ))
            ]);

            // 5. Process Payment History Data & Dynamic Sub-document References safely
            const payHistoryDataPromises = payHistorySnapshot.docs.map(async (doc1) => {
                const docData = doc1.data();

                // Safeguard references in case document connections are broken or null
                const paymode1Snap = docData.paymode1 ? await getDoc(doc(db, "paymentmodes", docData.paymode1)) : null;
                const paymode2Snap = docData.paymode2 ? await getDoc(doc(db, "paymentmodes", docData.paymode2)) : null;
                const bookingSnap = docData.bookingid ? await getDoc(doc(db, "bookings", docData.bookingid)) : null;

                const paymode1 = paymode1Snap?.exists() ? paymode1Snap.data().paymode : "";
                const paymode2 = paymode2Snap?.exists() ? paymode2Snap.data().paymode : "";

                // Handle timestamp parsing safely
                const createdAt = docData.createdAt ? docData.createdAt.toDate() : new Date();
                const formattedDate = moment(createdAt).format('DD-MM-YYYY HH:mm:ss');

                let bData = { rooms: [], custName: "" };
                if (bookingSnap && bookingSnap.exists()) {
                    const bookingDocData = bookingSnap.data();
                    bData = {
                        rooms: bookingDocData.rooms || [],
                        checkinDate: bookingDocData.checkindate || null,
                        checkoutDate: bookingDocData.checkoutdate || null,
                        status: bookingDocData.status || "Booked",
                        custName: bookingDocData.customers?.custname || bookingDocData.customers?.custName || "Guest"
                    };
                }
                return {
                    id: doc1.id,
                    data: docData,
                    paymode1,
                    paymode2,
                    createdat: createdAt,
                    formattedCreatedAt: formattedDate,
                    bookingData: bData
                };
            });

            const payHistoryData = await Promise.all(payHistoryDataPromises);
            setpayhistory(payHistoryData);

            // 6. Compute Mode-Wise Totals based on filtered Range Transactions
            const paymentModesSnapshot = await getDocs(collection(db, "paymentmodes"));
            const paymentData = paymentModesSnapshot.docs.map((pModeDoc) => {
                const paymodeId = pModeDoc.id;
                const paymode = pModeDoc.data().paymode || "";

                const filteredPayHistory = payHistoryData.filter(item => item.data.paymode1 === paymodeId || item.data.paymode2 === paymodeId);
                const totalAmount = filteredPayHistory.reduce((sum, item) => {
                    const amount1 = item.data.paymode1 === paymodeId ? (item.data.amount1 || 0) : 0;
                    const amount2 = item.data.paymode2 === paymodeId ? (item.data.amount2 || 0) : 0;
                    return sum + amount1 + amount2;
                }, 0);
                return { id: paymodeId, paymode, totalamount: totalAmount.toFixed(2) };
            });

            setpaymodes(paymentData);
            const overallTotal = paymentData.reduce((sum, data) => sum + parseFloat(data.totalamount), 0);
            settotal(overallTotal.toFixed(0));

            // 7. Sanitize Check-in Data (Drops missing or corrupted bookingid entries before processing)
            const checkinDetailsData = checkinDetailsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const uniqueCheckinDetails = Array.from(
                new Map(
                    checkinDetailsData
                        .filter(item => item.bookingid && typeof item.bookingid === 'string')
                        .map(item => [item.bookingid, item])
                ).values()
            );

            const bookingIds = uniqueCheckinDetails.map(doc => doc.bookingid);

            if (bookingIds.length === 0) {
                setloading(false);
                return;
            }

            // 8. Dynamic Chunking to avoid Firestore 'in' query limit constraints (max 30 elements limit)
            const chunkArray = (array, size) => {
                const result = [];
                for (let i = 0; i < array.length; i += size) result.push(array.slice(i, i + size));
                return result;
            };

            const validBookingIds = bookingIds.filter(id => id !== undefined && id !== null && id !== "");

            if (validBookingIds.length === 0) {
                setloading(false);
                return;
            }

            const bookingIdChunks = chunkArray(validBookingIds, 30);
            let bookingsDocs = [];
            await Promise.all(bookingIdChunks.map(async (chunk) => {
                const q = query(collection(db, "bookings"), where("__name__", "in", chunk));
                const snap = await getDocs(q);
                bookingsDocs.push(...snap.docs);
            }));

            const bookingsDataMap = bookingsDocs.reduce((acc, doc) => {
                acc[doc.id] = { id: doc.id, ...doc.data() };
                return acc;
            }, {});

            const paymentHistoryMap = payHistoryData.reduce((acc, pay) => {
                const bId = pay.data.bookingid;
                if (!acc[bId]) acc[bId] = [];
                acc[bId].push(pay);
                return acc;
            }, {});

            // 9. Process and Split Records into Active & Canceled Lists
            const activeBookings = [];
            const canceledBookings = [];
            let runningCanceledSum = 0;

            await Promise.all(uniqueCheckinDetails.map(async (checkinDetail) => {
                const bData = bookingsDataMap[checkinDetail.bookingid] || null;
                if (!bData) return;

                let fetchedUser = "Unknown User";
                if (checkinDetail.uid) {
                    const userSnap = await getDoc(doc(db, "users", checkinDetail.uid));
                    if (userSnap.exists()) fetchedUser = userSnap.data().username || "Unnamed User";
                }

                const roomamount = bData.rooamount || bData.roomamount || 0;

                // Calculate dynamic days stayed inside the selected range timeline safely
                const checkInMoment = moment(bData.checkindate, 'YYYY-MM-DD');
                const checkOutMoment = bData.checkoutdate ? moment(bData.checkoutdate, 'YYYY-MM-DD') : moment(endDateStr, 'YYYY-MM-DD');

                let dayDifference = checkOutMoment.diff(checkInMoment, 'days');
                if (isNaN(dayDifference) || dayDifference <= 0) {
                    dayDifference = 1;
                }

                const serviceamount = (parseFloat(bData?.serviceamount || 0) / parseFloat(bData.noofdays || 1)) || 0;
                let paymentHistory = paymentHistoryMap[checkinDetail.bookingid] || [];
                let paidAmount = paymentHistory.reduce((sum, pay) => sum + (pay.data.amount1 || 0) + (pay.data.amount2 || 0), 0);

                let totalamount = parseFloat(roomamount) + parseFloat(serviceamount);
                let alltoal = parseFloat(totalamount) * parseFloat(dayDifference);
                let remaining = parseFloat(alltoal) - parseFloat(paidAmount);

                const record = {
                    ...checkinDetail,
                    booking: bData,
                    username: fetchedUser,
                    serviceamount,
                    roomamount: parseFloat(roomamount).toFixed(0),
                    foodamount: 0,
                    totalamount: totalamount.toFixed(0),
                    alltoal: alltoal.toFixed(0),
                    paidamount: paidAmount.toFixed(0),
                    pending: remaining.toFixed(0),
                    todayDays: dayDifference,
                    paymentHistory
                };

                if (bData.status === "Canceled" || bData.status === "Cancelled") {
                    canceledBookings.push(record);
                    runningCanceledSum += alltoal;
                } else {
                    activeBookings.push(record);
                }
            }));

            setbookingData(activeBookings);
            setCanceledBookingData(canceledBookings);
            setTotalCanceledAmount(runningCanceledSum);

        } catch (error) {
            console.error("Critical error building report metrics:", error);
            Toast.fire({ icon: "error", title: `Error: ${error.message || "Failed to generate report window data metrics."}` });
        } finally {
            setloading(false);
        }
    };

    const handlePrint = useReactToPrint({ content: () => printRef.current });

    return (
        <div>
            <Menu />
            <div className="content-wrapper">
                <section className="content mt-2">
                    <div className="container-fluid">
                        <button className="btn btn-secondary ">|| All Booking Report ||</button><br /><br />
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card card-primary card-outline">
                                    <div className="card-body">
                                        <div className='row'>
                                            <div className='col-sm-4'>
                                                <label>From Date</label>
                                                <input type='date' value={fromDate} onChange={(e) => setfromDate(e.target.value)} className='form-control' />
                                            </div>
                                            <div className='col-sm-4'>
                                                <label>To Date</label>
                                                <input type='date' value={toDate} onChange={(e) => settoDate(e.target.value)} className='form-control' />
                                            </div>
                                            <div className='col-sm-3'>
                                                <br />
                                                <button className='btn btn-info' onClick={getReport} disabled={loading}>
                                                    {loading ? "Loading..." : "Get Report"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card card-primary card-outline" ref={printRef}>
                                    <div className="card-body">
                                        <div className="row invoice-info">
                                            <div className="col-sm-12 invoice-col text-center">
                                                <address>
                                                    <h4>Dasara Executive</h4>
                                                    Chatrapati Shivaji Maharaj Chouk, Tuljapur, Dist - Dharashiv, MH - 413601<br />
                                                    Phone: +91 9975294940 | Email: dasaraexecutive@gmail.com<br />
                                                    Website: www.dasaraexecutive.com
                                                </address>
                                            </div>
                                        </div>

                                        <div className='row'>
                                            <table className='customers'>
                                                <tbody>
                                                    <tr>
                                                        <th style={{ textAlign: "center" }}>From Date : {fromDate ? moment(fromDate).format('DD/MM/YYYY') : ""}</th>
                                                        <th style={{ textAlign: "center" }}><h5>BOOKING & COLLECTION REPORT</h5></th>
                                                        <th style={{ textAlign: "center" }}>To Date : {toDate ? moment(toDate).format('DD/MM/YYYY') : ""}</th>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            {/* ACTIVE BOOKINGS TABLE */}
                                            <table className="customers">
                                                <thead>
                                                    <tr>
                                                        <th style={{ backgroundColor: "#E0FCFE" }} colSpan={12}>Active Bookings</th>
                                                    </tr>
                                                    <tr>
                                                        <th>Sr No</th>
                                                        <th>Customer Name</th>
                                                        <th>Checkin Date</th>
                                                        <th>Checkout Date</th>
                                                        <th>Days</th>
                                                        <th>Rooms</th>
                                                        <th style={{ textAlign: "right" }}>Amount</th>
                                                        <th style={{ textAlign: "right" }}>Extra</th>
                                                        <th style={{ textAlign: "right" }}>Food</th>
                                                        <th style={{ textAlign: "right" }}>Total</th>
                                                        <th style={{ textAlign: "center" }}>Payments</th>
                                                        <th>Username</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bookingData.length > 0 ? bookingData.map((item, index) => (
                                                        <tr key={index}>
                                                            <td width="50">{index + 1}</td>
                                                            <td style={{ textTransform: "capitalize" }}>{item.booking?.customers?.custname || item.booking?.customers?.custName || "N/A"}</td>
                                                            <td>{item.checkindate ? moment(item.checkindate).format('DD/MM/YYYY') : ""} {item.checkintime || ""}</td>
                                                            <td>{item.checkoutdate ? moment(item.checkoutdate).format('DD/MM/YYYY') : ""} {item.checkouttime || ""}</td>
                                                            <td>{item.todayDays}</td>
                                                            <td>
                                                                {item.booking?.rooms?.map((rm, idx) => (
                                                                    <span key={idx}>&nbsp; {rm.roomname} ({parseFloat(rm.discount || 0)}%)<br /></span>
                                                                ))}
                                                            </td>
                                                            <td style={{ textAlign: "right" }}>{item.roomamount}</td>
                                                            <td style={{ textAlign: "right" }}>{item.serviceamount.toFixed(0)}</td>
                                                            <td style={{ textAlign: "right" }}>{item.foodamount}</td>
                                                            <td style={{ textAlign: "right" }}>{item.totalamount}</td>
                                                            <td style={{ textAlign: "right" }}>
                                                                {item.paymentHistory.length > 0 ? item.paymentHistory.map((pmnt, pIdx) => (
                                                                    <div key={pIdx}>
                                                                        {parseFloat(pmnt.data.amount1 || 0) > 0 && <span><b>{pmnt.paymode1} : {parseFloat(pmnt.data.amount1).toFixed(0)}</b><br /></span>}
                                                                        {parseFloat(pmnt.data.amount2 || 0) > 0 && <span><b>{pmnt.paymode2} : {parseFloat(pmnt.data.amount2).toFixed(0)}</b></span>}
                                                                    </div>
                                                                )) : <b style={{ color: "red" }}>Pending</b>}
                                                            </td>
                                                            <td>{item.username}</td>
                                                        </tr>
                                                    )) : <tr><td colSpan="12" style={{ textAlign: "center" }}>No active bookings found for this timeline.</td></tr>}
                                                </tbody>
                                            </table>

                                            {/* CANCELED BOOKINGS TABLE */}
                                            <table className="customers" style={{ marginTop: "20px" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ backgroundColor: "#FFEBEB", color: "#A94442" }} colSpan={8}>Canceled Bookings Log</th>
                                                    </tr>
                                                    <tr>
                                                        <th>Sr No</th>
                                                        <th>Customer Name</th>
                                                        <th>Checkin Date</th>
                                                        <th>Checkout Date</th>
                                                        <th>Rooms Assigned</th>
                                                        <th style={{ textAlign: "right" }}>Room Cost Value</th>
                                                        <th style={{ textAlign: "right" }}>Total Lost Amount</th>
                                                        <th>Processed User</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {canceledBookingData.length > 0 ? canceledBookingData.map((item, index) => (
                                                        <tr key={index} style={{ backgroundColor: "#FFF5F5" }}>
                                                            <td>{index + 1}</td>
                                                            <td style={{ textTransform: "capitalize", color: "#C9302C" }}>{item.booking?.customers?.custname || item.booking?.customers?.custName || "N/A"}</td>
                                                            <td>{item.checkindate ? moment(item.checkindate).format('DD/MM/YYYY') : ""}</td>
                                                            <td>{item.checkoutdate ? moment(item.checkoutdate).format('DD/MM/YYYY') : ""}</td>
                                                            <td>
                                                                {item.booking?.rooms?.map((rm, idx) => (
                                                                    <span key={idx}>{rm.roomname} <br /></span>
                                                                ))}
                                                            </td>
                                                            <td style={{ textAlign: "right" }}>{item.roomamount}</td>
                                                            <td style={{ textAlign: "right", fontWeight: "bold", color: "#C9302C" }}>{item.totalamount}</td>
                                                            <td>{item.username}</td>
                                                        </tr>
                                                    )) : <tr><td colSpan="8" style={{ textAlign: "center" }}>No canceled bookings for this duration.</td></tr>}
                                                </tbody>
                                            </table>

                                            {/* COLLECTION SUMMARY TABLES */}
                                            <table className="customers" style={{ marginTop: "20px" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ backgroundColor: "#E0FCFE" }} colSpan={paymodes.filter(p => parseFloat(p.totalamount) > 0).length + 1}>Paymode Wise Realized Collection</th>
                                                    </tr>
                                                    <tr>
                                                        <th>Paymodes</th>
                                                        {paymodes.map((item, index) => parseFloat(item.totalamount) > 0 && <th key={index}>{item.paymode}</th>)}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <th>Amount</th>
                                                        {paymodes.map((item, index) => parseFloat(item.totalamount) > 0 && <th key={index}>{item.totalamount}</th>)}
                                                    </tr>
                                                </tbody>
                                            </table>

                                            {/* FINANCIAL SUMMARY BALANCES */}
                                            <table className='customers' style={{ marginTop: "15px" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ backgroundColor: "#DFF0D8", color: "#3C763D", width: "50%" }}>
                                                            <h5>Total Realized Net Sale: Rs. {total} /-</h5>
                                                        </th>
                                                        <th style={{ backgroundColor: "#F2DEDE", color: "#A94442", width: "50%" }}>
                                                            <h5>Total Canceled Value: Rs. {totalCanceledAmount.toFixed(0)} /-</h5>
                                                        </th>
                                                    </tr>
                                                </thead>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <center>
                                    <button className='btn btn-success' onClick={handlePrint}>Print Report</button>
                                </center>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default Allbookingreport;